import React, { useState } from "react";
import { useTranslation } from "react-i18next";

// @mui material components
import { Stack } from "@mui/material";
import Button from "@mui/material/Button";
import RightDrawer from "../../../../components/RightDrawer";
import { DrawerFormSection } from "../../../../components/DrawerForm";
import { drawerButtonCancelProps, drawerButtonSaveProps } from "../../../../lib/ui/pageButtons";
import { customerService } from "../../../../services/customerService";
import { sourceSystemService } from "../../../../services/sourceSystemService";
import CustomerModel from "../../model";
import SourceSystemModel from "../../../source-systems/model";
import { parseCustomerUsersField } from "../../../../lib/customer/users";
import { formatCustomerPhone, isCustomerPhoneFormatValid } from "../../../../lib/customer/phone";
import { notifyValidationWarning } from "../../../../utils/notification";
import CreateCustomerDialogBody from "./CreateCustomerDialogBody";
import EditCustomerDialogBody from "./EditCustomerDialogBody";
import CreateSourceSystemDialogBody from "../../../source-systems/ui/components/CreateSourceSystemDialogBody";
import EditSourceSystemDialogBody from "../../../source-systems/ui/components/EditSourceSystemDialogBody";

type WizardMode = "create" | "edit";

type WizardOpenPayload = {
  mode: WizardMode;
  customer?: any;
};

const CustomerSourceSystemWizardDialog = React.forwardRef((props: any, ref: any) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<WizardMode>("create");
  const [customer, setCustomer] = useState<any>({});
  const [sourceSystem, setSourceSystem] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Shared logo state for both Customer and Source System
  const [sharedLogoBase64, setSharedLogoBase64] = useState<string>('');
  const [sharedLogoPreview, setSharedLogoPreview] = useState<string | null>(null);

  const showSnack = (opts: any) => {
    window.dispatchEvent(new CustomEvent("showGlobalNotification", { detail: opts }));
  };

  const close = () => {
    setOpen(false);
  };

  React.useImperativeHandle(ref, () => ({
    showDialog(payload: WizardOpenPayload) {
      const nextMode = payload?.mode || "create";
      const nextCustomer = payload?.customer || {};

      setMode(nextMode);
      setCustomer(nextCustomer);
      setSourceSystem(null);
      setOpen(true);
      
      // Reset shared logo when opening dialog
      setSharedLogoBase64(nextCustomer?.logo || '');
      setSharedLogoPreview(nextCustomer?.logo ? `data:image/png;base64,${nextCustomer.logo}` : null);

      // Load source system for existing customer in edit mode
      if (nextMode === "edit" && nextCustomer?.id) {
        loadSourceSystemForCustomer(nextCustomer.id, nextCustomer.name);
      }
    },
    closeDialog: close,
  }));

  const loadSourceSystemForCustomer = async (customerId: string, customerName?: string) => {
    try {
      const res = await sourceSystemService.getSourceSystemsByCustomerId(customerId);
      const content = (res as any)?.content || [];
      const existing = content.length > 0 ? content[0] : null;

      setSourceSystem({
        ...(existing || {}),
        customerId: customerId,
        customerName: customerName,
        lockCustomer: true,
      });
    } catch (e) {
      setSourceSystem({
        customerId: customerId,
        customerName: customerName,
        lockCustomer: true,
      });
    }
  };

  const getFormJson = (formEl: HTMLFormElement) => {
    const formData = new FormData(formEl);
    return Object.fromEntries(formData.entries());
  };

  const handleSave = async () => {
    const formEl = document.getElementById("customer-wizard-form") as HTMLFormElement | null;
    if (!formEl) return;

    if (!formEl.reportValidity()) {
      return;
    }

    const formJson: any = getFormJson(formEl);
    const users = parseCustomerUsersField(formJson.users);
    const phone = formatCustomerPhone(formJson.phone);
    const mobilePhone = formatCustomerPhone(formJson.mobilePhone);

    if (users.length === 0) {
      notifyValidationWarning(t("common.users"), t("common.atLeastOneUser"));
      return;
    }

    if (!isCustomerPhoneFormatValid(phone) || !isCustomerPhoneFormatValid(mobilePhone)) {
      notifyValidationWarning(t("common.validationError"), t("customers.phoneInvalid"));
      return;
    }

    setSubmitting(true);
    try {
      let effectiveCustomerId = customer?.id;

      // Step 1: Create or Update Customer
      const customerPayload: any = {
        name: formJson.name,
        users,
        logo: formJson.logo || undefined,
        description: formJson.description || undefined,
        email: formJson.email || undefined,
        phone: phone || undefined,
        mobilePhone: mobilePhone || undefined,
        address: formJson.address || undefined,
        pointOfContact: formJson.pointOfContact || undefined,
        status: formJson.status || "ACTIVE",
        startWorkTime: formJson.startWorkTime || undefined,
        endWorkTime: formJson.endWorkTime || undefined,
        timeZone: formJson.timeZone || undefined,
        locale: formJson.locale || undefined,
      };

      if (mode === "edit") {
        customerPayload.id = customer?.id;
        await customerService.updateCustomer(customerPayload);
        showSnack(
          await CustomerModel.message(
            CustomerModel.SUCCESS,
            t("common.updateRequest"),
            t("customers.updatedSuccess"),
            Date.now
          )
        );
      } else {
        const created = await customerService.createCustomer(customerPayload);
        effectiveCustomerId = created?.id;
        showSnack(
          await CustomerModel.message(
            CustomerModel.SUCCESS,
            t("common.createRequest"),
            t("customers.createdSuccess"),
            Date.now
          )
        );
      }

      // Step 2: Create or Update Source System (linked to the customer)
      const sourceSystemPayload: any = {
        id: sourceSystem?.id || undefined,
        name: formJson.sourceName,
        logo: formJson.sourceLogo || "",
        contacts: formJson.contacts ? JSON.parse(formJson.contacts as any) : [],
        sources: formJson.sources ? JSON.parse(formJson.sources as any) : [],
        status: formJson.sourceStatus,
        customerId: effectiveCustomerId,
      };

      if (sourceSystem?.id) {
        await sourceSystemService.updateSourceSystem(sourceSystemPayload);
        showSnack(
          await SourceSystemModel.message(
            SourceSystemModel.SUCCESS,
            t("common.updateRequest"),
            t("sourceSystems.updatedSuccess"),
            Date.now
          )
        );
      } else {
        await sourceSystemService.createSourceSystem(sourceSystemPayload);
        showSnack(
          await SourceSystemModel.message(
            SourceSystemModel.SUCCESS,
            t("common.createRequest"),
            t("sourceSystems.createdSuccess"),
            Date.now
          )
        );
      }

      window.dispatchEvent(new CustomEvent("refreshDataTable"));
      close();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RightDrawer
      open={open}
      onClose={close}
      width="lg"
      title={mode === "edit" ? t("customers.wizardEdit") : t("customers.wizardNew")}
      subtitle={`${t("customers.sectionCustomer")} · ${t("customers.sectionSourceSystem")}`}
      component="form"
      componentProps={{
        id: "customer-wizard-form",
        onSubmit: (e: React.FormEvent) => e.preventDefault(),
      }}
      footer={
        <>
          <Button {...drawerButtonCancelProps} type="button" onClick={close} disabled={submitting}>
            {t("common.cancel")}
          </Button>
          <Button {...drawerButtonSaveProps} type="button" onClick={handleSave} disabled={submitting}>
            {submitting ? t("common.saving") : t("common.save")}
          </Button>
        </>
      }
    >
      <Stack spacing={2.5}>
        <DrawerFormSection title={t("customers.sectionCustomer")}>
          {mode === "edit" ? (
            <EditCustomerDialogBody
              {...customer}
              sharedLogoBase64={sharedLogoBase64}
              sharedLogoPreview={sharedLogoPreview}
              onLogoChange={(base64: string, preview: string | null) => {
                setSharedLogoBase64(base64);
                setSharedLogoPreview(preview);
              }}
            />
          ) : (
            <CreateCustomerDialogBody
              {...customer}
              sharedLogoBase64={sharedLogoBase64}
              sharedLogoPreview={sharedLogoPreview}
              onLogoChange={(base64: string, preview: string | null) => {
                setSharedLogoBase64(base64);
                setSharedLogoPreview(preview);
              }}
            />
          )}
        </DrawerFormSection>

        <DrawerFormSection title={t("customers.sectionSourceSystem")}>
          {sourceSystem?.id ? (
            <EditSourceSystemDialogBody {...sourceSystem} sharedLogoPreview={sharedLogoPreview} />
          ) : (
            <CreateSourceSystemDialogBody
              customerId={customer?.id}
              customerName={customer?.name}
              lockCustomer={true}
              sharedLogoPreview={sharedLogoPreview}
            />
          )}
        </DrawerFormSection>
      </Stack>
    </RightDrawer>
  );
});

export default CustomerSourceSystemWizardDialog;
